/**
 * Right-click menus.
 *
 * One system for every menu in the app: a list of items anchored to a point or
 * an element, flipped when it would run off an edge, closed by Escape, a click
 * outside, or picking something.
 *
 * `useContextMenu` returns props you spread onto anything that should have a
 * menu. Touch has no right button, so a half-second hold does the same thing.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';

export type MenuItem =
  | '-'
  | {
      label: string;
      icon?: string;
      danger?: boolean;
      disabled?: boolean;
      onClick: () => void;
    };

type At = { x: number; y: number };

/* ---------------------------------------------------------- the menu */

export function MenuPanel({ at, items, onClose }: {
  at: At;
  items: MenuItem[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // Measured after mount, then placed: the menu has to exist before its size
  // is known, and guessing produces a visible jump on the first frame.
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const box = node.getBoundingClientRect();
    let left = at.x;
    let top = at.y;
    if (left + box.width > window.innerWidth - 8) left = Math.max(8, at.x - box.width);
    if (top + box.height > window.innerHeight - 8) top = Math.max(8, at.y - box.height);
    setPos({ left, top });
  }, [at.x, at.y]);

  /*
   * Keyboard: the first item takes focus when the menu opens, arrows move
   * between items, and focus goes back to whatever opened it on close - so a
   * menu opened from a button is usable without a mouse.
   */
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const t = setTimeout(() => {
      ref.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus({ preventScroll: true });
    }, 0);
    return () => {
      clearTimeout(t);
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, []);

  function onArrows(e: React.KeyboardEvent) {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
    const list = [...(ref.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? [])];
    if (!list.length) return;
    e.preventDefault();
    const i = list.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      e.key === 'Home' ? 0 :
        e.key === 'End' ? list.length - 1 :
          e.key === 'ArrowDown' ? (i + 1) % list.length : (i - 1 + list.length) % list.length;
    list[next]?.focus();
  }

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (ref.current?.contains(e.target as Node)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    }
    // Deferred so the very click that opened this does not immediately close it.
    const t = setTimeout(() => {
      document.addEventListener('mousedown', onDown, true);
      document.addEventListener('keydown', onKey, true);
      window.addEventListener('blur', onClose);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      onKeyDown={onArrows}
      data-keeps-card
      style={{
        left: pos?.left ?? at.x,
        top: pos?.top ?? at.y,
        // Hidden for the one frame before it is measured.
        visibility: pos ? 'visible' : 'hidden'
      }}
      className="fixed z-[420] min-w-[214px] origin-top-left overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] border border-line bg-surface-2 p-1.5 shadow-[var(--shadow-3),0_0_0_1px_rgba(0,0,0,.4)] backdrop-blur-[2px] animate-[menuin_140ms_var(--ease-out-soft)]"
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, i) =>
        item === '-' ? (
          // Full bleed: the rule runs the whole width of the menu rather than
          // stopping inside its padding, so it reads as dividing the menu into
          // groups instead of as a short line floating between two rows.
          <div key={i} role="separator" className="-mx-1.5 my-1.5 h-px bg-line" />
        ) : (
          <button
            key={i}
            type="button"
            role="menuitem"
            disabled={item.disabled}
            onClick={(e) => {
              // The menu is layered over whatever it was opened from. Without
              // this the click carries on to the card underneath and opens it
              // instead of running the action you picked.
              e.preventDefault();
              e.stopPropagation();
              onClose();
              item.onClick();
            }}
            className={cn(
              'flex w-full items-center gap-3 rounded-[calc(10px*var(--radius-mul,1))] px-2.5 py-[7px] text-left text-[13px] font-semibold',
              'transition-[background,color] duration-120',
              'disabled:pointer-events-none disabled:opacity-40',
              item.danger
                ? 'text-bad hover:bg-[color-mix(in_srgb,var(--bad)_16%,transparent)]'
                : 'text-dim hover:bg-accent-soft hover:text-ink'
            )}
          >
            {item.icon ? <Icon name={item.icon} size={17} className="flex-none opacity-80" /> : null}
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
          </button>
        )
      )}
      <style>{`@keyframes menuin { from { opacity:0; transform: scale(.96) translateY(-4px) } to { opacity:1; transform:none } }`}</style>
    </div>,
    document.body
  );
}

/* ------------------------------------------------------- the hook */

/**
 * Gives a component a right-click menu.
 *
 * `build` runs when the menu opens, not on every render, so the items always
 * reflect the state at the moment you right-clicked.
 */
export function useContextMenu(build: () => MenuItem[] | null | undefined) {
  const [at, setAt] = useState<At | null>(null);
  const [items, setItems] = useState<MenuItem[]>([]);
  const holdRef = useRef<number | null>(null);
  /** Where the finger went down, so a scroll can be told from a hold. */
  const startRef = useRef<{ x: number; y: number } | null>(null);

  const open = useCallback((x: number, y: number) => {
    const list = build();
    if (!list || !list.length) return;
    setItems(list);
    setAt({ x, y });
  }, [build]);

  const close = useCallback(() => setAt(null), []);

  const props = {
    onContextMenu: (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      open(e.clientX, e.clientY);
    },
    /*
     * Touch: hold to open.
     *
     * With a little slack, because a finger never holds perfectly still.
     * Cancelling on any movement at all meant the hold usually failed and
     * the menu felt broken; ten pixels is enough to tell a hold from a
     * scroll.
     */
    onTouchStart: (e: React.TouchEvent) => {
      const t = e.touches[0];
      if (!t) return;
      const { clientX, clientY } = t;
      startRef.current = { x: clientX, y: clientY };
      holdRef.current = window.setTimeout(() => {
        startRef.current = null;
        // A short buzz, where the device offers one, so you know the hold
        // registered without having to look.
        try { navigator.vibrate?.(12); } catch { /* not supported */ }
        open(clientX, clientY);
      }, 420);
    },
    onTouchEnd: () => {
      if (holdRef.current) clearTimeout(holdRef.current);
      startRef.current = null;
    },
    onTouchMove: (e: React.TouchEvent) => {
      const t = e.touches[0];
      const from = startRef.current;
      if (!t || !from) return;
      if (Math.abs(t.clientX - from.x) < 10 && Math.abs(t.clientY - from.y) < 10) return;
      if (holdRef.current) clearTimeout(holdRef.current);
      startRef.current = null;
    }
  };

  const menu: ReactNode = at ? <MenuPanel at={at} items={items} onClose={close} /> : null;

  return { props, menu, openAt: open, close };
}
